/**
 * In-feature habit progress — same nouns as Express habit templates.
 * Do not invent a unit from featureId; use these registry templates.
 *
 * express_app/services/growthEngine/config/copy/stage3.js
 *   recipes_habit_progress → '{n} of {limit} dishes costed'
 *   par_levels_habit_progress → '{n} of {limit} pars set'
 *   stock_takes_habit_progress → '{n} of {limit} counts done'
 * express_app/services/growthEngine/config/copy/stage2.js
 *   invoices_habit_progress → '{n} of {limit} invoices uploaded'
 *
 * Standing is cap-1 on Free (no habit intent). In-feature still uses
 * `{n} of {limit} standing orders` — not “slots used”, and never “Free”
 * on paid.
 *
 * `n of limit` is Free-only. Pass `isFree: false` on paid plans and the line
 * is a plain count (“12 dishes costed”) — a plan cap is not progress.
 */

export const GROWTH_ENGINE_HABIT_PROGRESS_TEMPLATES = Object.freeze({
  recipes: '{n} of {limit} dishes costed',
  par_levels: '{n} of {limit} pars set',
  stock_takes: '{n} of {limit} counts done',
  invoice_uploads: '{n} of {limit} invoices uploaded',
  standing_orders: '{n} of {limit} standing orders',
})

export const toHabitCountTemplate = template =>
  String(template || '').replace(/\{n\}\s*of\s*\{limit\}/g, '{n}')

/**
 * Resolve `{n} of {limit} …`. No cap → no line (paid unlimited, no upgrade tone).
 * `isFree: false` → `{n} …` with the real count, never the plan cap.
 */
export function formatGrowthEngineHabitProgress(
  template,
  { n = 0, limit, isFree } = {},
) {
  if (!template) return null
  const used = Math.max(0, Number(n) || 0)
  if (isFree === false) {
    return toHabitCountTemplate(template).replace(/\{n\}/g, String(used))
  }
  if (limit == null || limit === '') return null
  const cap = Number(limit)
  if (!Number.isFinite(cap) || cap <= 0) return null
  const shown = Math.min(used, cap)
  return String(template)
    .replace(/\{n\}/g, String(shown))
    .replace(/\{limit\}/g, String(cap))
}

export function formatRecipesHabitProgress(n, limit, { isFree } = {}) {
  return formatGrowthEngineHabitProgress(
    GROWTH_ENGINE_HABIT_PROGRESS_TEMPLATES.recipes,
    { n, limit, isFree },
  )
}

export function formatParLevelsHabitProgress(n, limit, { isFree } = {}) {
  return formatGrowthEngineHabitProgress(
    GROWTH_ENGINE_HABIT_PROGRESS_TEMPLATES.par_levels,
    { n, limit, isFree },
  )
}

export function formatStockTakesHabitProgress(n, limit, { isFree } = {}) {
  return formatGrowthEngineHabitProgress(
    GROWTH_ENGINE_HABIT_PROGRESS_TEMPLATES.stock_takes,
    { n, limit, isFree },
  )
}

export function formatInvoiceUploadsHabitProgress(n, limit, { isFree } = {}) {
  return formatGrowthEngineHabitProgress(
    GROWTH_ENGINE_HABIT_PROGRESS_TEMPLATES.invoice_uploads,
    { n, limit, isFree },
  )
}

export function formatStandingOrdersHabitProgress(n, limit, { isFree } = {}) {
  return formatGrowthEngineHabitProgress(
    GROWTH_ENGINE_HABIT_PROGRESS_TEMPLATES.standing_orders,
    { n, limit, isFree },
  )
}
